import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
    Aangifte, BokkzAlgDataService, IncomeTaxEntity, OmschrijvingEnBedrag
} from '@snelstart-ventures/fiscaal/domain-logic';
import { BehaviorSubject, filter, Subscription } from 'rxjs';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { BokkzIbDataService } from '@snelstart-ventures/fiscaal/domain-logic';
import { SelectFormControlItem, SharedUiFormControlsModule } from '@snelstart/ui-form-controls';
import { optionsLandcodesZonderNLD } from '../../gedeelde-componenten/gedeelde-componenten.component';
import { Event, NavigationEnd, Router } from '@angular/router';
import { SharedUiButtonModule } from '@snelstart/ui-button';
import { SharedUiDetailsModule } from '@snelstart/ui-details';
import { CommonModule } from '@angular/common';

@Component({
    selector: 'snelstart-venture-fiscaal-feature-verrekenbare-verliezen',
    templateUrl: './verrekenbare-verliezen.component.html',
    styleUrls: ['./verrekenbare-verliezen.component.scss'],
    imports: [
        SharedUiButtonModule,
        SharedUiDetailsModule,
        ReactiveFormsModule,
        SharedUiFormControlsModule,
        CommonModule
    ]
})
export class VerrekenbareVerliezenComponent implements OnInit, OnDestroy {
    private routerSubscription: Subscription | undefined;
    private incomeTaxEntitySubscription: Subscription | undefined;
    private clientEntitySubscription: Subscription | undefined;
    private isSaving = false;
    @Input() public id!: string;
    public clientId!: string;
    public aangifte = {} as Aangifte;
    public incomeTaxEntity = {} as IncomeTaxEntity;

    public optionsJarenBox1: SelectFormControlItem<string>[] = [];
    public optionsJarenBox2: SelectFormControlItem<string>[] = [];

    public partnerBoolean = {} as boolean;
    @Input() partner = new BehaviorSubject<boolean>(false);
    private partnerAangifte = {} as Aangifte;

    public isDefAangifte = {} as boolean;

    private focusedElementId: string | null = null;

    public verrekenbareVerliezenFG = {} as FormGroup;

    private generateYearOptions() {
        const aangifteJaar = this.incomeTaxEntity?.aangifte?.aangifteJaar;
        if (aangifteJaar) {
            // Box 1: 6 jaar carry-forward
            this.optionsJarenBox1 = this.createYearOptions(aangifteJaar, 9);

            // Box 2: 9 jaar carry-forward
            this.optionsJarenBox2 = this.createYearOptions(aangifteJaar, 6);
        }
    }

    private createYearOptions(aangifteJaar: number, yearsBack: number): SelectFormControlItem<string>[] {
        const options: SelectFormControlItem<string>[] = [];
        const startYear = aangifteJaar - yearsBack;
        const endYear = aangifteJaar - 1;

        for (let year = endYear; year >= startYear; year--) {
            options.push({
                value: `Jaar ${year}`,
                label: `Jaar ${year}`
            });
        }

        return options;
    }

    get onverrekendeVerliezenBox1(): FormArray {
        return this.verrekenbareVerliezenFG.get('onverrekendeVerliezenBox1') as FormArray;
    }

    addRijVerliesBox1(): void {
        this.onverrekendeVerliezenBox1.push(this.createOnverrekendeVerliezenBox1FormGroup());
    }

    verwijderRijVerliesBox1(index: number): void {
        this.onverrekendeVerliezenBox1.removeAt(index);
        this.verrekenbareVerliezenFG.updateValueAndValidity();
        this.verrekenbareVerliezenFG.markAsDirty();
        this.saveForm();
    }

    createOnverrekendeVerliezenBox1FormGroup(verlies?: OmschrijvingEnBedrag): FormGroup {
        return this.fb.group(({
            omschrijving: verlies ? verlies.omschrijving : undefined,
            bedrag: verlies ? verlies.bedrag : undefined
        } as OmschrijvingEnBedrag));
    }

    get onverrekendeVerliezenBox2(): FormArray {
        return this.verrekenbareVerliezenFG.get('onverrekendeVerliezenBox2') as FormArray;
    }

    addRijVerliesBox2(): void {
        this.onverrekendeVerliezenBox2.push(this.createOnverrekendeVerliezenBox2FormGroup());
    }

    verwijderRijVerliesBox2(index: number): void {
        this.onverrekendeVerliezenBox2.removeAt(index);
        this.verrekenbareVerliezenFG.updateValueAndValidity();
        this.verrekenbareVerliezenFG.markAsDirty();
        this.saveForm();
    }

    createOnverrekendeVerliezenBox2FormGroup(verlies?: OmschrijvingEnBedrag): FormGroup {
        return this.fb.group(({
            omschrijving: verlies ? verlies.omschrijving : undefined,
            bedrag: verlies ? verlies.bedrag : undefined
        } as OmschrijvingEnBedrag));
    }

    get belastingkortingBox2(): FormArray {
        return this.verrekenbareVerliezenFG.get('belastingkortingBox2') as FormArray;
    }

    addRijBelastingkortingBox2(): void {
        this.belastingkortingBox2.push(this.createBelastingkortingBox2FormGroup());
    }

    verwijderRijBelastingkortingBox2(index: number): void {
        this.belastingkortingBox2.removeAt(index);
        this.verrekenbareVerliezenFG.updateValueAndValidity();
        this.verrekenbareVerliezenFG.markAsDirty();
        this.saveForm();
    }

    createBelastingkortingBox2FormGroup(verlies?: OmschrijvingEnBedrag): FormGroup {
        return this.fb.group(({
            omschrijving: verlies ? verlies.omschrijving : undefined,
            bedrag: verlies ? verlies.bedrag : undefined
        } as OmschrijvingEnBedrag));
    }

    get onverrekendeBuitenlandseBronheffing(): FormArray {
        return this.verrekenbareVerliezenFG.get('onverrekendeBuitenlandseBronheffing') as FormArray;
    }

    addRijOnverrekendeBuitenlandseBronheffing(): void {
        this.onverrekendeBuitenlandseBronheffing.push(this.createOnverrekendeBuitenlandseBronheffingFormGroup());
    }

    verwijderRijOnverrekendeBuitenlandseBronheffing(index: number): void {
        this.onverrekendeBuitenlandseBronheffing.removeAt(index);
        this.verrekenbareVerliezenFG.updateValueAndValidity();
        this.verrekenbareVerliezenFG.markAsDirty();
        this.saveForm();
    }

    createOnverrekendeBuitenlandseBronheffingFormGroup(verlies?: OmschrijvingEnBedrag): FormGroup {
        return this.fb.group(({
            omschrijving: verlies ? verlies.omschrijving : undefined,
            bedrag: verlies ? verlies.bedrag : undefined
        } as OmschrijvingEnBedrag));
    }


    constructor(public dataService: BokkzIbDataService, public dataAlgService: BokkzAlgDataService,
                private router: Router,
                private fb: FormBuilder) {
        this.verrekenbareVerliezenFG = this.fb.group({
            onverrekendeVerliezenBox1: this.fb.array<OmschrijvingEnBedrag>([]),
            onverrekendeVerliezenBox2: this.fb.array<OmschrijvingEnBedrag>([]),
            belastingkortingBox2: this.fb.array<OmschrijvingEnBedrag>([]),
            onverrekendeBuitenlandseBronheffing: this.fb.array<OmschrijvingEnBedrag>([])
        });
    }


    public ngOnInit(): void {
        this.incomeTaxEntitySubscription = this.dataService.incomeTaxEntity.subscribe((incomeTaxEntity) => {
            this.incomeTaxEntity = incomeTaxEntity;
            if (incomeTaxEntity.aangifte) {
                const aangifte = incomeTaxEntity.aangifte as Aangifte;
                this.aangifte = aangifte;
                this.partnerAangifte = aangifte.partnerAangifte ? aangifte.partnerAangifte : {} as Aangifte;
                this.updateForm();
                this.partner.subscribe((partner) => {
                    this.partnerBoolean = partner;
                    this.verrekenbareVerliezenFG.reset();
                    this.updateForm();
                });
                this.generateYearOptions();
            }
        });
        this.routerSubscription = this.router.events.pipe(filter((event: Event): event is NavigationEnd => event instanceof NavigationEnd))
            .subscribe((event: NavigationEnd) => {
                this.handleUrlChange(event.urlAfterRedirects);
            });
        this.clientEntitySubscription = this.dataAlgService.client.subscribe((client) => {
            this.clientId = client.sstRelationId!;
        });
    }

    ngOnDestroy(): void {
        if (this.routerSubscription) {
            this.routerSubscription.unsubscribe(); // Unsubscribe to avoid memory leaks
        }
        if (this.incomeTaxEntitySubscription) {
            this.incomeTaxEntitySubscription.unsubscribe();
        }
        if (this.clientEntitySubscription) {
            this.clientEntitySubscription.unsubscribe();
        }
        if (this.clientEntitySubscription) {
            this.clientEntitySubscription.unsubscribe(); // Unsubscribe from income tax entity
        }
    }

    handleUrlChange(newUrl: string) {
        if (!this.isSaving && this.hasUnsavedChanges()) {
            this.isSaving = true;
            this.dataService.postIncomeTaxEntity(this.clientId, false, this.incomeTaxEntity);
            this.isSaving = false;
        }
    }

    private hasUnsavedChanges(): boolean {
        return this.verrekenbareVerliezenFG.dirty;
    }

    private updateForm() {
        // box1
        let verliesBox1Lijst = this.aangifte?.verliescompensatie?.onverrekendeVerliezenBox1 || [];
        this.partner.subscribe((partner) => {
            if (partner) {
                verliesBox1Lijst = this.aangifte?.partnerAangifte?.verliescompensatie?.onverrekendeVerliezenBox1 || [];
            }
        });
        const focusedControl = this.focusedElementId ? document.getElementById(this.focusedElementId) as HTMLElement : null;
        const formArrayBox1 = this.verrekenbareVerliezenFG.get('onverrekendeVerliezenBox1') as FormArray;

        while (formArrayBox1.length > verliesBox1Lijst.length) {
            formArrayBox1.removeAt(formArrayBox1.length - 1);
        }
        for (let i = 0; i < verliesBox1Lijst.length; i++) {
            if (i < formArrayBox1.length) {
                const control = formArrayBox1.at(i) as FormGroup;
                control.patchValue(verliesBox1Lijst[i]);
            } else {
                const box1 = this.createOnverrekendeVerliezenBox1FormGroup(verliesBox1Lijst[i]);
                formArrayBox1.push(box1);
            }
        }

        // box2
        let verliesBox2Lijst = this.aangifte?.verliescompensatie?.onverrekendeVerliezenBox2 || [];
        this.partner.subscribe((partner) => {
            if (partner) {
                verliesBox2Lijst = this.aangifte?.partnerAangifte?.verliescompensatie?.onverrekendeVerliezenBox2 || [];
            }
        });
        const formArrayBox2 = this.verrekenbareVerliezenFG.get('onverrekendeVerliezenBox2') as FormArray;

        while (formArrayBox2.length > verliesBox2Lijst.length) {
            formArrayBox2.removeAt(formArrayBox2.length - 1);
        }
        for (let i = 0; i < verliesBox2Lijst.length; i++) {
            if (i < formArrayBox2.length) {
                const control = formArrayBox2.at(i) as FormGroup;
                control.patchValue(verliesBox2Lijst[i]);
            } else {
                const box2 = this.createOnverrekendeVerliezenBox2FormGroup(verliesBox2Lijst[i]);
                formArrayBox2.push(box2);
            }
        }

        // korting box2
        let kortingBox2Lijst = this.aangifte?.verliescompensatie?.belastingkortingBox2 || [];
        this.partner.subscribe((partner) => {
            if (partner) {
                kortingBox2Lijst = this.aangifte?.partnerAangifte?.verliescompensatie?.belastingkortingBox2 || [];
            }
        });
        const formArrayKortingBox2 = this.verrekenbareVerliezenFG.get('belastingkortingBox2') as FormArray;

        while (formArrayKortingBox2.length > kortingBox2Lijst.length) {
            formArrayKortingBox2.removeAt(formArrayKortingBox2.length - 1);
        }
        for (let i = 0; i < kortingBox2Lijst.length; i++) {
            if (i < formArrayKortingBox2.length) {
                const control = formArrayKortingBox2.at(i) as FormGroup;
                control.patchValue(kortingBox2Lijst[i]);
            } else {
                const kortingBox2 = this.createBelastingkortingBox2FormGroup(kortingBox2Lijst[i]);
                formArrayKortingBox2.push(kortingBox2);
            }
        }

        // onverrekende Buitenlandse Bronheffing
        let bronheffingLijst = this.aangifte?.verliescompensatie?.onverrekendeBuitenlandseBronheffing || [];
        this.partner.subscribe((partner) => {
            if (partner) {
                bronheffingLijst = this.aangifte?.partnerAangifte?.verliescompensatie?.onverrekendeBuitenlandseBronheffing || [];
            }
        });
        const formArrayBronheffing = this.verrekenbareVerliezenFG.get('onverrekendeBuitenlandseBronheffing') as FormArray;

        while (formArrayBronheffing.length > bronheffingLijst.length) {
            formArrayBronheffing.removeAt(formArrayBronheffing.length - 1);
        }
        for (let i = 0; i < bronheffingLijst.length; i++) {
            if (i < formArrayBronheffing.length) {
                const control = formArrayBronheffing.at(i) as FormGroup;
                control.patchValue(bronheffingLijst[i]);
            } else {
                const bronheffing = this.createOnverrekendeBuitenlandseBronheffingFormGroup(bronheffingLijst[i]);
                formArrayBronheffing.push(bronheffing);
            }
        }

        if (focusedControl && document.contains(focusedControl)) {
            focusedControl.focus();
        }
    }

    saveForm(): void {
        if (!this.partnerBoolean) {
            this.incomeTaxEntity.aangifte = {
                ...this.aangifte,
                verliescompensatie: {
                    ...this.aangifte.verliescompensatie,
                    onverrekendeVerliezenBox1: this.verrekenbareVerliezenFG.value.onverrekendeVerliezenBox1,
                    onverrekendeVerliezenBox2: this.verrekenbareVerliezenFG.value.onverrekendeVerliezenBox2,
                    belastingkortingBox2: this.verrekenbareVerliezenFG.value.belastingkortingBox2,
                    onverrekendeBuitenlandseBronheffing: this.verrekenbareVerliezenFG.value.onverrekendeBuitenlandseBronheffing
                }
            } as Aangifte;
            this.dataService.updateIncomeTaxEntity(this.incomeTaxEntity);
        } else {
            this.incomeTaxEntity.aangifte = {
                ...this.aangifte,
                partnerAangifte: {
                    ...this.aangifte.partnerAangifte,
                    verliescompensatie: {
                        onverrekendeVerliezenBox1: this.verrekenbareVerliezenFG.value.onverrekendeVerliezenBox1,
                        onverrekendeVerliezenBox2: this.verrekenbareVerliezenFG.value.onverrekendeVerliezenBox2,
                        belastingkortingBox2: this.verrekenbareVerliezenFG.value.belastingkortingBox2,
                        onverrekendeBuitenlandseBronheffing: this.verrekenbareVerliezenFG.value.onverrekendeBuitenlandseBronheffing
                    }
                }
            } as Aangifte;
            this.dataService.updateIncomeTaxEntity(this.incomeTaxEntity);
        }
    }

    protected readonly optionsLandcodes = optionsLandcodesZonderNLD;
}


